// src/components/UploadLostItemForm.js
import React, { useState } from 'react';
import { saveItemToDynamoDB } from '../dynamoService';

const UpIco  = () => <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>;
const TagIco = () => <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>;
const PinIco = () => <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>;
const CalIco = () => <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
const UsoIco = () => <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
const PhIco  = () => <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.63A2 2 0 012 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>;

/* ── Field wrapper ── */
const Field = ({ label, children }) => (
    <div style={{ marginBottom: 20 }}>
        {label && <label className="field-label">{label}</label>}
        {children}
    </div>
);

/* ── Type toggle ── */
const TypeBtn = ({ val, emoji, title, sub, current, onSelect }) => {
    const active = current === val;
    const isLost = val === 'lost';
    return (
        <button type="button" onClick={() => onSelect(val)} style={{
            flex: 1, padding: '18px 16px', borderRadius: 14, cursor: 'pointer',
            border: active
                ? `2px solid ${isLost ? '#b33315' : 'var(--sage)'}`
                : '2px solid var(--border)',
            background: active
                ? (isLost ? '#fde8e4' : 'var(--sage-l)')
                : 'var(--white)',
            display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left',
            transition: 'all 0.15s', fontFamily: 'var(--sans)',
        }}>
            <span style={{ fontSize: 24 }}>{emoji}</span>
            <div>
                <div style={{
                    fontFamily: 'var(--serif)', fontSize: 16, fontWeight: 400,
                    color: active ? (isLost ? '#b33315' : '#2e5c3a') : 'var(--ink-2)',
                }}>{title}</div>
                <div style={{ fontSize: 12, color: 'var(--ink-4)', marginTop: 1 }}>{sub}</div>
            </div>
        </button>
    );
};

function UploadLostItemForm() {
    const [form, setForm] = useState({
        itemType: 'lost', itemName: '', description: '',
        location: '', dateLost: '', reporterName: '', contactInfo: '',
    });
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [status, setStatus] = useState(null);
    const [drag, setDrag] = useState(false);

    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
    const handle = e => set(e.target.name, e.target.value);

    const processFile = f => {
        if (!f) return;
        setFile(f);
        const r = new FileReader();
        r.onloadend = () => setPreview(r.result);
        r.readAsDataURL(f);
    };

    const onSubmit = async e => {
        e.preventDefault();
        const { itemName, description, location, dateLost, reporterName, contactInfo } = form;
        if (!itemName || !description || !location || !dateLost || !reporterName || !contactInfo || !file) {
            setStatus({ ok: false, msg: 'Please fill in all fields and upload an image.' });
            return;
        }
        setSubmitting(true); setStatus(null);
        try {
            const { message } = await saveItemToDynamoDB(form, file);
            setStatus({ ok: true, msg: message || 'Report submitted successfully!' });
            setForm({ itemType: 'lost', itemName: '', description: '', location: '', dateLost: '', reporterName: '', contactInfo: '' });
            setFile(null); setPreview(null);
        } catch (err) {
            setStatus({ ok: false, msg: err.message || 'Upload failed. Please try again.' });
        } finally { setSubmitting(false); }
    };

    const isLost = form.itemType === 'lost';

    return (
        <div className="page-wrap" style={{ paddingTop: 60 }}>
            {/* Page header */}
            <div style={{ background: 'var(--white)', borderBottom: '1px solid var(--border)', padding: '48px 0 36px' }}>
                <div className="container">
                    <div className="label-xs" style={{ marginBottom: 10 }}>File a report</div>
                    <h1 className="display" style={{ fontSize: 'clamp(30px, 4vw, 52px)', marginBottom: 8 }}>
                        Report an item
                    </h1>
                    <p style={{ color: 'var(--ink-3)', fontSize: 15 }}>
                        Help reunite belongings with their owners. The more detail, the better.
                    </p>
                </div>
            </div>

            <div className="container" style={{ padding: '44px 32px 80px' }}>
                <div style={{
                    maxWidth: 680, margin: '0 auto',
                    background: 'var(--white)', borderRadius: 24,
                    border: '1px solid var(--border)',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
                    padding: 'clamp(24px, 4vw, 44px)',
                }}>
                    <form onSubmit={onSubmit}>
                        {/* Type selector */}
                        <Field label="I am reporting…">
                            <div style={{ display: 'flex', gap: 10 }}>
                                <TypeBtn val="lost" emoji="🔍" title="Lost item" sub="I lost something" current={form.itemType} onSelect={v => set('itemType', v)} />
                                <TypeBtn val="found" emoji="📦" title="Found item" sub="I found something" current={form.itemType} onSelect={v => set('itemType', v)} />
                            </div>
                        </Field>

                        <div className="hr" />

                        {/* Image upload */}
                        <Field label="Photo of the item">
                            <div
                                onDragOver={e => { e.preventDefault(); setDrag(true); }}
                                onDragLeave={() => setDrag(false)}
                                onDrop={e => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files[0]; if (f?.type.startsWith('image/')) processFile(f); }}
                                style={{
                                    borderRadius: 14, overflow: 'hidden', cursor: 'pointer',
                                    border: `2px dashed ${drag ? 'var(--accent)' : preview ? '#bcd9c6' : 'rgba(26,23,20,0.15)'}`,
                                    background: drag ? 'var(--accent-l)' : preview ? 'var(--sage-l)' : 'var(--cream)',
                                    transition: 'all 0.15s',
                                }}
                            >
                                {preview ? (
                                    <div style={{ position: 'relative' }}>
                                        <img src={preview} alt="Preview" style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }} />
                                        <label style={{
                                            position: 'absolute', inset: 0, cursor: 'pointer',
                                            background: 'rgba(0,0,0,0.35)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            opacity: 0, transition: 'opacity 0.15s',
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.opacity = 1}
                                        onMouseLeave={e => e.currentTarget.style.opacity = 0}
                                        >
                                            <div style={{ color: '#fff', textAlign: 'center' }}>
                                                <div style={{ fontSize: 28, marginBottom: 6 }}>📷</div>
                                                <span style={{ fontSize: 14, fontWeight: 500 }}>Change image</span>
                                            </div>
                                            <input type="file" style={{ display: 'none' }} accept="image/*" onChange={e => processFile(e.target.files[0])} />
                                        </label>
                                    </div>
                                ) : (
                                    <label style={{ cursor: 'pointer', display: 'block' }}>
                                        <div style={{ padding: '36px 20px', textAlign: 'center' }}>
                                            <div style={{ color: 'var(--ink-4)', marginBottom: 10, display: 'flex', justifyContent: 'center' }}><UpIco /></div>
                                            <p style={{ fontWeight: 500, color: 'var(--ink-2)', marginBottom: 4 }}>
                                                Drop image here, or <span style={{ color: 'var(--accent)', textDecoration: 'underline' }}>browse</span>
                                            </p>
                                            <p style={{ fontSize: 12, color: 'var(--ink-4)' }}>PNG, JPG up to 5MB</p>
                                        </div>
                                        <input type="file" style={{ display: 'none' }} accept="image/*" onChange={e => processFile(e.target.files[0])} />
                                    </label>
                                )}
                            </div>
                        </Field>

                        <div className="hr" />

                        {/* Item name */}
                        <Field label={isLost ? 'What did you lose?' : 'What did you find?'}>
                            <div className="input-wrap">
                                <span className="input-ico"><TagIco /></span>
                                <input className="field-input icon-l"
                                    name="itemName" value={form.itemName} onChange={handle}
                                    placeholder={isLost ? 'e.g. Black wallet, iPhone 12' : 'e.g. Blue backpack, Gold ring'} required />
                            </div>
                        </Field>

                        {/* Description */}
                        <Field label="Description">
                            <textarea className="field-input" name="description" value={form.description} onChange={handle}
                                rows={4} style={{ resize: 'vertical', minHeight: 90 }}
                                placeholder={`Describe in detail — colour, brand, distinguishing marks${isLost ? '' : ', exact spot where found'}`} required />
                        </Field>

                        {/* Location + Date */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                            <Field label={isLost ? 'Where did you lose it?' : 'Where did you find it?'}>
                                <div className="input-wrap">
                                    <span className="input-ico"><PinIco /></span>
                                    <input className="field-input icon-l"
                                        name="location" value={form.location} onChange={handle}
                                        placeholder="e.g. Library, Block C" required />
                                </div>
                            </Field>
                            <Field label={isLost ? 'When?' : 'Date found?'}>
                                <div className="input-wrap">
                                    <span className="input-ico"><CalIco /></span>
                                    <input className="field-input icon-l" type="date"
                                        name="dateLost" value={form.dateLost} onChange={handle} required />
                                </div>
                            </Field>
                        </div>

                        <div className="hr" />

                        {/* Contact info notice */}
                        <div style={{
                            padding: '12px 16px', background: 'var(--cream)',
                            borderRadius: 10, border: '1px solid var(--border)',
                            fontSize: 13, color: 'var(--ink-3)', marginBottom: 20,
                            display: 'flex', alignItems: 'center', gap: 8,
                        }}>
                            🔒 <span>Contact info is only visible to <strong style={{ color: 'var(--ink-2)' }}>signed-in users</strong>.</span>
                        </div>

                        {/* Name + Contact */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                            <Field label="Your name">
                                <div className="input-wrap">
                                    <span className="input-ico"><UsoIco /></span>
                                    <input className="field-input icon-l"
                                        name="reporterName" value={form.reporterName} onChange={handle}
                                        placeholder="Full name" required />
                                </div>
                            </Field>
                            <Field label="Mobile or email">
                                <div className="input-wrap">
                                    <span className="input-ico"><PhIco /></span>
                                    <input className="field-input icon-l"
                                        name="contactInfo" value={form.contactInfo} onChange={handle}
                                        placeholder="How to reach you" required />
                                </div>
                            </Field>
                        </div>

                        {/* Status */}
                        {status && (
                            <div className={`alert ${status.ok ? 'alert-ok' : 'alert-err'}`} style={{ marginBottom: 16 }}>
                                <span>{status.ok ? '✓' : '⚠'}</span>
                                <span>{status.msg}</span>
                            </div>
                        )}

                        {/* Submit */}
                        <button type="submit" disabled={submitting} className="btn btn-ink btn-lg"
                            style={{ width: '100%', borderRadius: 12, marginTop: 4 }}>
                            {submitting ? (
                                <>
                                    <svg style={{ animation: 'spin 0.9s linear infinite' }} width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 12a9 9 0 11-6.219-8.56"/></svg>
                                    Submitting…
                                </>
                            ) : `Submit ${isLost ? 'lost' : 'found'} item report`}
                        </button>

                        <p style={{ textAlign: 'center', fontSize: 12, color: 'var(--ink-4)', marginTop: 14 }}>
                            By submitting you agree to our{' '}
                            <a href="#" style={{ color: 'var(--ink-3)', textDecoration: 'underline' }}>Terms of Service</a>.
                        </p>
                    </form>
                </div>
            </div>
        </div>
    );
}
export default UploadLostItemForm;